import { useApolloClient } from '@apollo/client'
import type { FccWaypointPickupAction } from '@backoffice-frontend/graphql'
import { PassengerAuthenticationState } from '@backoffice-frontend/graphql/subscription'
import isEqual from 'lodash/isEqual'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { Subscription } from 'zen-observable-ts'
import type { UsePassengerAuthenticationsWaypointFragment } from './usePassengerAuthentications.hook'
import { PassengerAuthenticationCardStateDocument } from './usePassengerAuthentications.subscription.hook'

type WaypointAction =
  UsePassengerAuthenticationsWaypointFragment['actions'][number]

const isPickupAction = (
  action: WaypointAction,
): action is FccWaypointPickupAction => {
  return action.__typename === 'FccWaypointPickupAction'
}

const usePassengerAuthenticationsContextValue = () => {
  const client = useApolloClient()
  const [passengerAuthentications, setPassengerAuthentications] =
    useState<Record<string, PassengerAuthenticationState | undefined>>()
  const tripIds = useRef<Array<string>>([])
  const subscriptions = useRef<Array<Subscription>>([])

  const getPassengerAuthentication = (tripId: string) => {
    return passengerAuthentications?.[tripId]
  }

  const subscribeToPassengerAuthentications = (
    waypoint?: UsePassengerAuthenticationsWaypointFragment,
    vehicleId?: string,
  ) => {
    const newTripIds: Array<string> = []

    waypoint?.actions.forEach(action => {
      if (isPickupAction(action) && action.trip?.id) {
        newTripIds.push(action.trip.id)
      }
    })

    if (
      newTripIds.length > 0 &&
      vehicleId &&
      !isEqual(newTripIds, tripIds.current)
    ) {
      subscriptions.current.forEach(subscription => {
        subscription.unsubscribe()
      })

      subscriptions.current = newTripIds.map(tripId =>
        client
          .subscribe({
            query: PassengerAuthenticationCardStateDocument,
            variables: { fleetVehicle: vehicleId, tripId },
          })
          .subscribe({
            next({ data }) {
              setPassengerAuthentications(authentications => {
                return {
                  ...authentications,
                  [tripId]:
                    data?.passengerAuthenticationState.state ?? undefined,
                }
              })
            },
            error(error) {
              console.error('Error in subscription', error)
            },
          }),
      )
    }

    tripIds.current = newTripIds
  }

  useEffect(() => {
    return () => {
      subscriptions.current.forEach(subscription => {
        subscription.unsubscribe()
      })
    }
  }, [])

  const hasPassengerAuthentications =
    passengerAuthentications && Object.keys(passengerAuthentications).length > 0

  const isAllPassengersCheckedIn =
    hasPassengerAuthentications &&
    Object.values(passengerAuthentications).every(isConfirmed)

  const isAllManualNoShowConfirmed =
    hasPassengerAuthentications &&
    Object.values(passengerAuthentications).every(isManualNoShow)

  const isAllPassengersCancelledTrip =
    hasPassengerAuthentications &&
    Object.values(passengerAuthentications).every(isCancelled)

  const isSomePassengerCheckedIn =
    hasPassengerAuthentications &&
    Object.values(passengerAuthentications).some(isConfirmed)

  const isSomeManualNoShowConfirmed =
    hasPassengerAuthentications &&
    Object.values(passengerAuthentications).some(
      state => state === PassengerAuthenticationState.NoShowTriggered,
    )

  const isSomePassengerCancelledTrip =
    hasPassengerAuthentications &&
    Object.values(passengerAuthentications).some(
      state => state === PassengerAuthenticationState.Cancelled,
    )

  const isAllAuthenticationsCancelled =
    (isSomeManualNoShowConfirmed &&
      isSomePassengerCancelledTrip &&
      !isSomePassengerCheckedIn) ||
    isAllPassengersCancelledTrip ||
    isAllManualNoShowConfirmed

  return {
    isAllAuthenticationsCancelled,
    isAllPassengersCheckedIn:
      isAllPassengersCheckedIn ||
      (isSomePassengerCheckedIn && isSomePassengerCancelledTrip) ||
      (isSomePassengerCheckedIn && isSomeManualNoShowConfirmed),
    getPassengerAuthentication,
    subscribeToPassengerAuthentications,
  }
}

type PassengerAuthenticationsContextValue = ReturnType<
  typeof usePassengerAuthenticationsContextValue
>

const PassengerAuthenticationsContext =
  createContext<PassengerAuthenticationsContextValue | null>(null)

export const PassengerAuthenticationsProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const passengerAuthenticationsContextValue =
    usePassengerAuthenticationsContextValue()

  return (
    <PassengerAuthenticationsContext.Provider
      value={passengerAuthenticationsContextValue}
    >
      {children}
    </PassengerAuthenticationsContext.Provider>
  )
}

export const usePassengerAuthentications = () => {
  const passengerAuthenticationsContextValue = useContext(
    PassengerAuthenticationsContext,
  )

  if (passengerAuthenticationsContextValue === null) {
    throw new Error(
      'usePassengerAuthentications must be used within PassengerAuthenticationsProvider',
    )
  }

  return passengerAuthenticationsContextValue
}

const isManualNoShow = (authenticationState?: PassengerAuthenticationState) =>
  authenticationState === PassengerAuthenticationState.NoShowTriggered

const isCancelled = (authenticationState?: PassengerAuthenticationState) =>
  authenticationState !== undefined &&
  authenticationState === PassengerAuthenticationState.Cancelled

const isConfirmed = (authenticationState?: PassengerAuthenticationState) =>
  authenticationState !== undefined &&
  authenticationState === PassengerAuthenticationState.Confirmed
